import { useEffect } from 'react';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { SidebarProvider, useSidebar } from './contexts/SidebarContext';
import Header from './components/Header';
import Sidebar from './components/Sidebar';
import RecentProjects from './components/RecentProjects';
import ExploreRenders from './components/ExploreRenders';
import StartDesigning from './components/StartDesigning';
import Projects from './components/Projects';
import Photorealistic from './components/Photorealistic';
import Supplier from './components/Supplier';
import UploadMaterial from './components/UploadMaterial';
import Search from './components/Search';
import Login from './components/Login';
import LandingPage from './components/LandingPage';
import Preloader from './components/Preloader';

/**
 * App Component
 * Main dashboard application matching the DX LIVING design
 * Layout: Header, Sidebar, and Main Content Area
 * Shows Login page when unauthenticated.
 */

const AppContent = () => {
  const { currentView, roomId, setCurrentView, setRoomId } = useSidebar();

  const syncViewFromPath = () => {
    const path = window.location.pathname;
    const designMatch = path.match(/^\/design\/(\d+)\/?$/);
    if (designMatch) {
      const id = Number(designMatch[1]);
      setCurrentView('Photorealistic');
      setRoomId(id);
      return;
    }
    if (path === '/projects' || path.startsWith('/projects/')) {
      setCurrentView('Projects');
      setRoomId(null);
      return;
    }
    if (path === '/start-designing' || path.startsWith('/start-designing/')) {
      setCurrentView('StartDesigning');
      setRoomId(null);
      return;
    }
    if (path === '/supplier' || path.startsWith('/supplier/')) {
      setCurrentView('Supplier');
      setRoomId(null);
      return;
    }
    if (path === '/upload-product' || path.startsWith('/upload-product/')) {
      setCurrentView('UploadMaterial');
      setRoomId(null);
      return;
    }
    if (path === '/search' || path.startsWith('/search?')) {
      setCurrentView('Search');
      setRoomId(null);
      return;
    }
    setCurrentView('Dashboard');
    setRoomId(null);
  };

  // On mount + browser back/forward: sync state from URL.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    syncViewFromPath();
    const onPopState = () => syncViewFromPath();
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, [setCurrentView, setRoomId]);

  // Keep the URL in sync with the current main view.
  useEffect(() => {
    if (typeof window === 'undefined') return;

    if (currentView === 'Photorealistic' && roomId != null) {
      const newPath = `/design/${roomId}`;
      if (window.location.pathname !== newPath) {
        window.history.replaceState({}, '', newPath);
      }
    } else if (currentView === 'Supplier') {
      if (window.location.pathname !== '/supplier') {
        window.history.replaceState({}, '', '/supplier');
      }
    } else if (currentView === 'UploadMaterial') {
      if (window.location.pathname !== '/upload-product') {
        window.history.replaceState({}, '', '/upload-product');
      }
    } else if (currentView === 'Search') {
      if (window.location.pathname !== '/search') {
        window.history.pushState({}, '', '/search');
      }
    } else if (currentView === 'StartDesigning') {
      if (window.location.pathname !== '/start-designing') {
        // Use pushState so browser Back returns to previous page (e.g., /dashboard).
        window.history.pushState({}, '', '/start-designing');
      }
    } else if (currentView === 'Projects') {
      if (window.location.pathname !== '/projects') {
        window.history.pushState({}, '', '/projects');
      }
    } else if (currentView === 'Dashboard') {
      if (window.location.pathname !== '/dashboard') {
        window.history.replaceState({}, '', '/dashboard');
      }
    } else {
      if (
        window.location.pathname.startsWith('/design/') ||
        window.location.pathname === '/supplier' ||
        window.location.pathname.startsWith('/supplier/') ||
        window.location.pathname === '/start-designing' ||
        window.location.pathname.startsWith('/start-designing/') ||
        window.location.pathname === '/projects' ||
        window.location.pathname.startsWith('/projects/') ||
        window.location.pathname === '/upload-product' ||
        window.location.pathname.startsWith('/upload-product/') ||
        window.location.pathname === '/search' ||
        window.location.pathname === '/dashboard'
      ) {
        window.history.replaceState({}, '', '/');
      }
    }
  }, [currentView, roomId]);

  return (
    <div className="gradient-bg h-screen overflow-hidden flex flex-col">
      {/* Header - Fixed at top */}
      <Header />

      {/* Main Layout */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar - Fixed on left */}
        <Sidebar />

        {/* Main Content - Scrollable, with left margin for sidebar (no margin on Photorealistic) */}
        <main
          className={`flex-1 h-[calc(100vh-4rem)] overflow-y-auto transition-all duration-300 ${
            currentView === 'Photorealistic' ? 'p-0' : 'p-6 py-10'
          }`}
        >
          {currentView === 'Photorealistic' ? (
            <Photorealistic />
          ) : (
            <div className="max-w-full mx-auto">
              {currentView === 'StartDesigning' ? (
                <StartDesigning />
              ) : currentView === 'Projects' ? (
                <Projects />
              ) : currentView === 'Supplier' ? (
                <Supplier />
              ) : currentView === 'UploadMaterial' ? (
                <UploadMaterial />
              ) : currentView === 'Search' ? (
                <Search />
              ) : (
                <>
                  {/* Recent Projects Section */}
                  <RecentProjects />

                  {/* Explore Our Renders Section */}
                  <ExploreRenders />
                </>
              )}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

const AppShell = () => {
  const { isAuthenticated, isLoading, showPreloader, preloaderFadingOut } = useAuth();

  // When unauthenticated (after loading), keep protected routes on /login while allowing
  // public routes (/ and /login).
  useEffect(() => {
    if (typeof window === 'undefined' || isLoading) return;
    if (
      !isAuthenticated &&
      window.location.pathname !== '/' &&
      window.location.pathname !== '/login'
    ) {
      window.history.replaceState({}, '', '/login');
    }
  }, [isAuthenticated, isLoading]);

  // After successful login, redirect /login to /dashboard.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    if (
      isAuthenticated &&
      !showPreloader &&
      window.location.pathname === '/login'
    ) {
      window.history.replaceState({}, '', '/dashboard');
    }
  }, [isAuthenticated, showPreloader]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-pulse text-gray-500">Loading…</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    if (typeof window !== 'undefined' && window.location.pathname === '/') {
      return <LandingPage />;
    }
    return <Login />;
  }

  if (typeof window !== 'undefined' && window.location.pathname === '/') {
    return <LandingPage />;
  }

  if (showPreloader) {
    return <Preloader isFadingOut={preloaderFadingOut} />;
  }

  return (
    <SidebarProvider>
      <AppContent />
    </SidebarProvider>
  );
};

function App() {
  return (
    <AuthProvider>
      <AppShell />
    </AuthProvider>
  );
}

export default App;
